import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Stack } from '@mui/material';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';

type Breadcrumb = {
	breadcrumb: string;
	url: string;
};

type ReturnType = {
	hidePageTitle: boolean;
	hideBreadcrumb: boolean;
	breadcrumbs: Breadcrumb[];
	BreadcrumbsNavigation: ReactNode;
};

const formatBreadcrumb = (str: string): string => {
	if (str.length === 0) {
		return str;
	}
	return str.charAt(0).toUpperCase() + str.slice(1).replaceAll('-', ' ');
};

const getCrumbsAndUrl = (path: string): Breadcrumb[] => {
	const pathSegmentsArray = path
		.split('/')
		.filter((part) => part !== '') // skip first /slash
		.reduce((acc: Breadcrumb[], part) => {
			//Previous already accumulated url string from object + extend by next part of url or just first part of url from root url
			const url = acc.length > 0 ? `${acc[acc.length - 1].url}/${part}` : `/${part}`;
			acc.push({ breadcrumb: formatBreadcrumb(part), url }); // extend array by the newest part
			return acc;
		}, []);

	return pathSegmentsArray;
};

export const useBreadcrumbsNavigation = ({ separator = '/' }: { separator?: string | null } = {}): Readonly<ReturnType> => {
	const { pathname } = useLocation();
	const navigationList = getCrumbsAndUrl(pathname);

	const segments = navigationList.map(({ url, breadcrumb }, i) => (
		<Link
			key={url}
			href={url}
			variant={'body1'}
			fontSize={14}
			color={(theme) =>
				navigationList.length - 1 === i ? theme.palette[getNightOrDayPalette(theme)][900] : theme.palette[getNightOrDayPalette(theme)][500]
			}
			underline={'none'}
		>
			{breadcrumb}
		</Link>
	));
	return {
		hidePageTitle: getCrumbsAndUrl(pathname).length >= 3,
		hideBreadcrumb: getCrumbsAndUrl(pathname).length < 3,
		breadcrumbs: getCrumbsAndUrl(pathname),
		BreadcrumbsNavigation: (
			<Stack spacing={2}>
				<Breadcrumbs separator={separator}>{segments}</Breadcrumbs>
			</Stack>
		),
	};
};
